/* eslint-disable no-restricted-imports */
/* eslint-disable complexity */
/* eslint-disable no-trailing-spaces */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { STRING_TRUE } from '@/data/constants/catalog';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { useAllowableShippingModes } from '@/data/Content/_AllowableShippingModes';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { getInventory, useInventory } from '@/data/Content/_Inventory';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { getPromo, usePromo } from '@/data/Content/_Promos';
import { requisitionListItemUpdate } from '@/data/Content/_RequisitionList';
import { wishListUpdater } from '@/data/Content/_WishListDetails';
import { useWishRequisitionList } from '@/data/Content/_WishRequisitionList';
import { addToCartFetcher, BASE_ADD_2_CART_BODY } from '@/data/Content/Cart';
import {
	getProductBeforePaymentFromQuery,
	PRODUCT_BEFORE_PAYMENT_ATTRIBUTE,
} from '@/data/utils/productBeforePayment';
import { useCategory } from '@/data/Content/Category';
import { useNotifications } from '@/data/Content/Notifications';
import { getProduct, getProductByKeyType, useProduct } from '@/data/Content/Product';
import { EventsContext } from '@/data/context/events';
import { getGTMConfig } from '@/data/events/handlers/gtm';
import { getLocalization, useLocalization } from '@/data/Localization';
import { isB2BStore, useSettings } from '@/data/Settings';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { ContentProps } from '@/data/types/ContentProps';
import { ProductType, Selection } from '@/data/types/Product';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { useUser } from '@/data/User';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getParentCategoryFromSlashPath } from '@/data/utils/getParentCategoryFromSlashPath';
import {
	getAttrsByIdentifier,
	mapProductDetailsData,
	search,
} from '@/data/utils/mapProductDetailsData';
import { mapProductDetailsDataElectronico } from '@/data/utils/mapProductDetailsDataElectronico';
import { cartMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/cartMutatorKeyMatcher';
import { processError } from '@/data/utils/processError';
import { WishlistWishlistItem } from 'integration/generated/transactions/data-contracts';
import { setLocalStorageCart } from 'integration/utils/localStorageCart';
import { MouseEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { mutate } from 'swr';
import { EMPTY_PROD } from './OrderItemList';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { wishListRemoverOrItemRemover } from '@/data/Content/WishLists';
import { usePDPContext } from '@/data/context/PDP';
import {
	CLICK_SOURCE_LIST_NAME,
	CLICK_PRODUCT_VIEW_SOURCE,
} from '@/data/constants/clickProductView';
import { GTMProductViewSource } from '@/data/types/GTM';
import { isValidClickProductView } from '@/data/utils/isValidClickProductView';

export { getAttrsByIdentifier };

type Props = {
	partNumber: string;
	physicalStoreName?: string;
	isElectronico?: boolean;
};

const fetchLocalization = async ({ cache, context }: ContentProps, productId: string) =>
	await Promise.all([
		getPromo(cache, productId, context),
		getLocalization(cache, context.locale || 'en-US', 'productDetail'),
		getLocalization(cache, context.locale || 'en-US', 'CommerceEnvironment'),
		getLocalization(cache, context.locale || 'en-US', 'PriceDisplay'),
		getLocalization(cache, context.locale || 'en-US', 'Common'),
	]);

export const getProductDetails = async ({ cache, id, context }: ContentProps) => {
	const partNumber = id.toString();
	let product = await getProduct(cache, partNumber, context);
	if (product?.type === 'item' && product.parentCatalogEntryID) {
		// get parent product
		product = await getProductByKeyType(cache, 'id', product.parentCatalogEntryID, context);
		// get inventory for self
		await getInventory(cache, product?.partNumber ?? '', context);
	} else if (product?.type === 'product' || product?.type === 'variant') {
		// get inventory for first SKU
		await getInventory(cache, product.items?.at(0)?.partNumber as string, context);
	}
	await fetchLocalization({ cache, id, context }, product?.id ?? '');
};

export const useProductDetailsAddWishList = (product?: ProductType) => {
	const { onAddToWishlist } = useContext(EventsContext);
	const params = useExtraRequestParameters();
	const { settings } = useSettings();

	const addToWishList = useCallback(
		(partNumber: string, quantity: number, wishList: WishlistWishlistItem) => async () => {
			try {
				await wishListUpdater(true)(
					settings?.storeId as string,
					wishList.uniqueID as string,
					{ item: [{ partNumber, quantityRequested: quantity.toString() } as any] },
					{ addItem: true },
					params
				);

				product && onAddToWishlist(product, quantity);
			} catch (error) {
				throw error;
			}
		},
		[settings?.storeId, params, product, onAddToWishlist]
	);

	return { addToWishList };
};

export const useProductDetailsRemoveWishList = () => {
	const params = useExtraRequestParameters();
	const { settings } = useSettings();

	const removeToWishList = useCallback(
		(productId: string, wishList: WishlistWishlistItem) => async () => {
			try {
				await wishListRemoverOrItemRemover(true)(
					settings?.storeId as string,
					wishList.uniqueID as string,
					{ productId },
					params
				);
			} catch (error) {
				throw error;
			}
		},
		[settings?.storeId, params]
	);

	return { removeToWishList };
};

export const useProductDetailsAddToCart = (product?: ProductType, selection?: Selection) => {
	const params = useExtraRequestParameters();
	const { settings } = useSettings();
	const { user } = useUser();
	const router = useNextRouter();
	const routes = useLocalization('Routes');
	const { onAddToCart } = useContext(EventsContext);
	const { ga4, ua } = getGTMConfig(settings);
	const { category } = useCategory(
		ga4 || ua ? getParentCategoryFromSlashPath(product?.parentCatalogGroupID) : ''
	);
	const { pickupInStoreShipMode } = useAllowableShippingModes();
	const { showAddToCart, notifyError } = useNotifications();

	const { availability } = useInventory(selection?.sku?.partNumber ?? product?.partNumber);

	const loginStatus = !!user?.isLoggedIn;

	const loginRequired = useCallback(() => {
		if (!loginStatus && isB2BStore(settings)) {
			router.push(routes.Login.route.t());
			return true;
		} else {
			return false;
		}
	}, [loginStatus, router, routes, settings]);

	const addToCart = useCallback(
		async (
			event: MouseEvent<HTMLElement>,
			skipAlert?: boolean,
			orderItemExtendAttribute?: Array<{
				attributeName: string;
				attributeType?: string;
				attributeValue: string;
			}>,
			source?: GTMProductViewSource
		) => {
			if (loginRequired()) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			const newSelection = selection ?? {
				sku: product ?? EMPTY_PROD,
				quantity: 1,
				buyable: !!(product?.buyable === STRING_TRUE),
			};

			const { sku, quantity } = newSelection;

			if (sku?.partNumber) {
				let avail: ProductAvailabilityData | undefined;
				avail = availability?.find((a) => a.status);
				if (!avail) {
					avail = availability?.find((a) => a.physicalStoreStatus);
				}

				const orderItem = {
					productId: sku.id,
					partNumber: sku.partNumber,
					quantity: quantity.toString(),
					...(avail?.physicalStoreId && {
						physicalStoreId: avail.physicalStoreId,
						shipModeId: pickupInStoreShipMode?.shipModeId,
					}),
					orderItemExtendAttribute,
				};

				const data = { ...BASE_ADD_2_CART_BODY, orderItem: [orderItem] };
				try {
					await addToCartFetcher(true)(settings?.storeId ?? '', {}, data, params);

					if (!skipAlert) {
						showAddToCart();
					}

					await mutate(cartMutatorKeyMatcher(EMPTY_STRING), undefined);

					if (!loginStatus) {
						setLocalStorageCart([sku.partNumber]);
					}

					onAddToCart({
						gtm: {
							selection: newSelection,
							category,
							quantity,
							orgName: '', // TODO: specify selected org-name
							orgId: '', // TODO: specify selected org
							storeName: settings.storeName,
							settings,
							availability,
							product,
							source,
						},
					});

					scrollTo(0, 0);
				} catch (e) {
					notifyError(processError(e as TransactionErrorResponse));
				}
			}
		},
		[
			loginRequired,
			selection,
			availability,
			pickupInStoreShipMode?.shipModeId,
			settings,
			params,
			onAddToCart,
			category,
			notifyError,
			loginStatus,
			showAddToCart,
			product,
		]
	);

	return { addToCart, loginRequired };
};

const tallaOrder: Record<string, number> = {
	ech: 1,
	ch: 2,
	m: 3,
	g: 4,
	xg: 5,
};

export enum PERSONALIZATION_OPTIONS {
	PLAYER = 'player',
	CUSTOM = 'custom',
}

export const useProductDetails = (props: Props) => {
	const { onProductView } = useContext(EventsContext);
	const { showSuccessMessage, notifyError } = useNotifications();
	const success = useLocalization('success-message');
	const { user } = useUser();
	const { settings } = useSettings();
	const router = useNextRouter();
	const { langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { partNumber: pnFromPage, physicalStoreName } = props;
	const { product: inputCE, loading: loadingProduct } = useProduct({ id: pnFromPage });
	const { product: root, loading: loadingRoot } = useProduct({
		id: inputCE?.parentCatalogEntryID,
		isCEId: true,
		condition: inputCE?.type === 'item',
	});
	const { selection: initial, product } = useMemo(
		() => mapProductDetailsData(inputCE, root),
		[inputCE, root]
	);
	const { selection: initialElectronicos } = useMemo(
		() => mapProductDetailsDataElectronico(inputCE, root),
		[inputCE, root]
	);
	const [selection, setSelection] = useState<Selection>(initial as Selection);
	const [sizeSelection, setSizeSelection] = useState<{
		loaded: boolean;
		selected: boolean;
	}>({
		loaded: false,
		selected: false,
	});
	const { ga4, ua } = getGTMConfig(settings);
	const { category } = useCategory(
		ga4 || ua ? getParentCategoryFromSlashPath(product?.parentCatalogGroupID) : ''
	);
	const { promos, promosDetails } = usePromo(product?.id);
	const loginStatus = !!user?.isLoggedIn;

	const values = inputCE?.items;
	const attributes = values?.map((item) => item.attributes);
	const id = values?.map((item) => item.id);

	const tallas = attributes?.map((item) => item?.find((talla) => talla?.identifier === 'Talla'));

	const partNumbersWithSize = id?.map((item, index) => ({
		id: item,
		talla: tallas?.[index]?.values[0].value,
	}));

	const sortedIdsWithSize = partNumbersWithSize?.sort((a, b) => {
		const tallaA = parseFloat(a.talla as string);
		const tallaB = parseFloat(b.talla as string);
		return tallaA - tallaB;
	});

	const partNumbers = inputCE?.items
		? inputCE?.items?.map((item) => item.partNumber).toString()
		: undefined;

	const isBundle = useMemo(() => inputCE?.type === 'bundle', [inputCE?.type]);
	const { productArg, selectionArg } = useMemo(() => {
		const defer = isBundle && !loadingProduct;
		return {
			productArg: defer ? undefined : product,
			selectionArg: defer ? undefined : selection ?? initial,
		};
	}, [isBundle, loadingProduct, product, selection, initial]);

	const {
		availability,
		hasInventory,
		loading: isLoadingInventory,
	} = useInventory(partNumbers, physicalStoreName);

	const itemsSortedBySize = useMemo(() => {
		if (!inputCE?.items) return [];

		const products = [...inputCE?.items].sort((a, b) => {
			const sizeA = findProductAttribute(a.attributes, 'Talla')?.value || '';
			const sizeB = findProductAttribute(b.attributes, 'Talla')?.value || '';

			const sizeALower = sizeA.toLowerCase();
			const sizeBLower = sizeB.toLowerCase();

			if (tallaOrder[sizeALower] && tallaOrder[sizeBLower]) {
				return tallaOrder[sizeALower] - tallaOrder[sizeBLower];
			}

			if (tallaOrder[sizeALower]) return -1;
			if (tallaOrder[sizeBLower]) return 1;

			const numA = Number(sizeA);
			const numB = Number(sizeB);

			if (!isNaN(numA) && !isNaN(numB)) {
				return numA - numB;
			}

			return sizeA.localeCompare(sizeB);
		});

		return products;
	}, [inputCE?.items]);

	const firstItemWithStock = useMemo(() => {
		const firstAvailable = itemsSortedBySize.find((item) => {
			const availabilityItem = availability?.find((ava) => ava.productId === item.id && ava.status);
			return availabilityItem !== undefined;
		});

		return firstAvailable;
	}, [itemsSortedBySize, availability]);

	const { pickupInStoreShipMode } = useAllowableShippingModes();
	const { wishLists, requisitionLists } = useWishRequisitionList();

	/**
	 * Defining attribute change handler
	 * @param identifier new attribute identifier
	 * @param value new attribute value
	 */
	const onAttributeChange = useCallback(
		(identifier: string, value: string) => {
			if (product?.items) {
				setSelection((selection) => {
					const asAttrs = product.items.map((s) => getAttrsByIdentifier(s.definingAttributes));
					const fullAttr = {
						...(selection ?? initial).attrsByIdentifier,
						[identifier]: value,
					};
					const { exact, best } = search(fullAttr, identifier, asAttrs);
					let sku: ProductType | undefined = undefined;
					let attrsByIdentifier: Record<string, string> | undefined = undefined;

					if (exact !== -1) {
						attrsByIdentifier = fullAttr;
						sku = product.items[exact];
					} else if (best !== -1) {
						attrsByIdentifier = { ...asAttrs[best] };
						sku = product.items[best];
					} else {
						sku = {} as ProductType;
					}
					return {
						...(selection ?? initial),
						buyable: !!(sku?.buyable === STRING_TRUE),
						sku,
						attrsByIdentifier,
					};
				});
				setSizeSelection({
					loaded: true,
					selected: true,
				});
			}
		},
		[initial, product?.items]
	);

	const viewSource = useMemo<GTMProductViewSource | undefined>(() => {
		const raw = router.query?.clickSource;
		const clickSource = Array.isArray(raw) ? raw[0] : raw;
		if (!isValidClickProductView(clickSource)) return undefined;
		return {
			item_list_id: clickSource!,
			item_list_name:
				CLICK_SOURCE_LIST_NAME[clickSource as CLICK_PRODUCT_VIEW_SOURCE] ?? clickSource,
		};
	}, [router.query?.clickSource]);

	const onSizeChange = useCallback(
		(identifier: string, value: string) => {
			if (!product?.items) {
				return;
			}

			const sku = product?.items.find((item) => {
				const talla = findProductAttribute(item.attributes, identifier)?.value;
				return talla === value;
			});

			const fullAttr = {
				...(selection ?? initial).attrsByIdentifier,
				[identifier]: value,
			};

			if (sku) {
				setSelection({
					...(selection ?? initial),
					buyable: !!(sku?.buyable === STRING_TRUE),
					sku,
					attrsByIdentifier: fullAttr,
				});
				setSizeSelection({
					loaded: true,
					selected: true,
				});
			}
		},
		[product?.items, selection, initial]
	);

	const onQuantity = useCallback(
		(quantity: number | null) =>
			setSelection((selection) => ({
				...(selection ?? initial),
				quantity: quantity ?? 0,
			})),
		[initial]
	);

	const { addToWishList } = useProductDetailsAddWishList(product);

	const { addToCart, loginRequired } = useProductDetailsAddToCart(productArg, selectionArg);

	const addToRequisitionList = useCallback(
		(requisitionListId: string) => async () => {
			const { sku, quantity } = selection ?? initial;
			if (sku?.partNumber) {
				try {
					await requisitionListItemUpdate(true)(
						{
							storeId: settings.storeId,
							langId,
							requisitionListId,
							data: { partNumber: sku.partNumber, quantity },
						},
						params
					);
					showSuccessMessage(success.addItemListSuccessfully.t({ v: sku.name }));
				} catch (e) {
					notifyError(processError(e as TransactionErrorResponse));
				}
			}
		},
		[initial, notifyError, params, selection, settings.storeId, showSuccessMessage, success, langId]
	);

	const onSelectionInitialElectronic = useCallback(() => {
		setSelection(initialElectronicos as Selection);
	}, [initialElectronicos]);

	// TODO Look into why this is firing after the FIRST time an attribute is changed, as well as the normal product load.
	// inputCE is changing, even though the product id is not.
	useEffect(() => {
		if (product && !isLoadingInventory) {
			onProductView({
				gtm: {
					product,
					storeName: settings.storeName,
					settings,
					availability,
					source: viewSource,
				},
			});
		}
	}, [product, isLoadingInventory]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		setSelection(initial as Selection);
		setSizeSelection({
			loaded: true,
			selected: false,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [langId]);

	const isBuyable = useMemo(() => product?.buyable === STRING_TRUE, [product?.buyable]);

	const handleAddToCart = async (event: MouseEvent<HTMLElement>, skipAlert?: boolean) => {
		if (!isBuyable) {
			notifyError('El producto no está disponible para la venta');
			return;
		}

		const orderItemExtendAttribute: Array<{
			attributeName: string;
			attributeType?: string;
			attributeValue: string;
		}> = [];

		const raw = router.query?.clickSource;
		const clickSource = Array.isArray(raw) ? raw[0] : raw;
		if (isValidClickProductView(clickSource)) {
			orderItemExtendAttribute.push({
				attributeName: 'recommended',
				attributeValue: clickSource!,
			});
		}

		const productBeforePayment = getProductBeforePaymentFromQuery(router.query);
		if (productBeforePayment) {
			orderItemExtendAttribute.push({
				attributeName: PRODUCT_BEFORE_PAYMENT_ATTRIBUTE,
				attributeValue: productBeforePayment,
			});
		}

		await addToCart(event, skipAlert, orderItemExtendAttribute, viewSource);
	};

	const { setProduct } = usePDPContext();

	useEffect(() => {
		if (product) {
			setProduct(product);
		}
	}, [product]); // eslint-disable-line react-hooks/exhaustive-deps

	return {
		addToCart: handleAddToCart,
		addToRequisitionList,
		addToWishList,
		availability,
		category,
		hasInventory,
		inputCE: selection,
		loading: loadingProduct || loadingRoot,
		firstItemWithStock,
		loginRequired,
		loginStatus,
		onAttributeChange,
		onQuantity,
		onSelectionInitialElectronic,
		partNumbers,
		partNumbersWithSize,
		pickupInStoreShipMode,
		product,
		promos,
		promosDetails,
		requisitionLists,
		selection: selection ?? initial,
		sizeAttribute: inputCE?.items,
		sortedIdsWithSize,
		wishLists,
		isLoadingInventory,
		isBundle,
		sizeSelection,
		isBuyable,
		onSizeChange,
	};
};;
